import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';
import type { AssetDetail } from '../asset-library-types';

const PATH = '/api/v2/asset/company/{companyId}/asset/{assetId}';

type Params = {
  companyId: Guid;
  assetId: Guid;
};

type Result = AssetDetail | null;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createGetAssetDetail = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const getAssetDetail = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await apiClient[PATH].get({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return getAssetDetail;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'get'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'get'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, assetId: params.assetId },
});

const convertResponse = async (response: Response): Promise<Result> => {
  const responseStatus = response.status;
  if (responseStatus === 404) {
    return null;
  }

  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  const result: AssetDetail = {
    id: data.id,
    name: data.name,
    size: data.size,
    uploadedAt: Number(data.uploadedAt),
    state: data.state,
    mediaUrl: data.mediaUrl,
    downloadUrl: data.downloadUrl,
    urlsExpireAt: Number(data.urlsExpireAt),
    thumbnailUrl: data.thumbnailUrl ?? null,
    dimensions: data.dimensions ?? null,
    attribution: data.attribution ?? null,
  };
  return result;
};
